import React, { useState, useEffect } from 'react';
import '../stylings/BucksRecipes.css';

function BucksRecipes() {
  const [isVisible, setIsVisible] = useState(false);
  const [activeCategory, setActiveCategory] = useState('bucks-products');
  const [searchTerm, setSearchTerm] = useState('');
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // API endpoint
  const API_ENDPOINT = 'https://s9351aaac7.execute-api.us-east-1.amazonaws.com/default/bucks-customer-recipes-get';
  
  // Animation on mount
  useEffect(() => {
    setIsVisible(true);
  }, []);
  
  // Fetch recipes from API
  const fetchRecipes = async (category, search) => {
    setLoading(true);
    setError(null);
    
    try {
      // Build query parameters
      let url = API_ENDPOINT;
      const params = [];
      
      if (category && category !== 'all') {
        params.push(`category=${category}`);
      }
      
      if (search && search.trim() !== '') {
        params.push(`search=${encodeURIComponent(search.trim())}`);
      }
      
      if (params.length > 0) {
        url += `?${params.join('&')}`;
      }
      
      // Fetch recipes
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        setRecipes(data.recipes);
      } else {
        throw new Error(data.message || 'Failed to fetch recipes');
      }
    } catch (err) {
      console.error('Error fetching recipes:', err);
      setError('Failed to load recipes. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle category change
  const handleCategoryChange = (category) => {
    setActiveCategory(category);
    setSearchTerm('');
    fetchRecipes(category, '');
  };
  
  // Handle search
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };
  
  // Handle search submission
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchRecipes(activeCategory, searchTerm);
  };
  
  // Fetch recipes when component mounts
  useEffect(() => {
    fetchRecipes(activeCategory, '');
  }, []);
  
  // Filter recipes based on active category
  const getFilteredRecipes = () => {
    return recipes.filter(recipe => {
      // Match the category
      if (recipe.frontendCategory === activeCategory) {
        return true;
      }
      return false;
    });
  };
  
  const filteredRecipes = getFilteredRecipes();

  return (
    <div className="bucks-recipes">
      {/* Recipes Hero Section */}
      <section className={`recipes-hero ${isVisible ? 'visible' : ''}`}>
        <div className="recipes-hero-content">
          <h1>Buck's Farmstand Recipes</h1>
          <p className="recipes-hero-subtitle">Discover the freshness of our products and creative ways to enjoy seasonal produce</p>
        </div>
      </section>

      {/* Recipe Categories Navigation */}
      <section className="recipe-categories">
        <div className="categories-container">
          <div className="categories-tabs">
            <button 
              className={`category-tab ${activeCategory === 'bucks-products' ? 'active' : ''}`}
              onClick={() => handleCategoryChange('bucks-products')}
            >
              Buck's Products
            </button>
            <button 
              className={`category-tab ${activeCategory === 'customer-recipes' ? 'active' : ''}`}
              onClick={() => handleCategoryChange('customer-recipes')}
            >
              Cook with Our Produce
            </button>
          </div>
          <form className="search-container" onSubmit={handleSearchSubmit}>
            <input
              type="text"
              placeholder="Search recipes..."
              value={searchTerm}
              onChange={handleSearch}
              className="search-input"
            />
            <button type="submit" className="search-btn" style={{ display: 'none' }}></button>
          </form>
        </div>
      </section>

      {/* Recipe Cards Grid */}
      <section className="recipes-grid-section">
        <div className="recipes-container">
          {loading ? (
            <div className="loading-recipes">
              <p>Loading recipes...</p>
            </div>
          ) : error ? (
            <div className="error-recipes">
              <p>{error}</p>
            </div>
          ) : filteredRecipes.length > 0 ? (
            <div className="recipes-grid">
              {filteredRecipes.map(recipe => (
                <div className="recipe-card" key={recipe.id}>
                  <div className="recipe-image-container">
                    <img src={recipe.image} alt={recipe.title} className="recipe-image" />
                  </div>
                  <div className="recipe-content">
                    <h3 className="recipe-title">{recipe.title}</h3>
                    <p className="recipe-description">{recipe.description}</p>
                    
                    {recipe.frontendCategory === 'customer-recipes' && (
                      <div className="recipe-meta">
                        <span className="recipe-difficulty">Difficulty: {recipe.difficulty}</span>
                        <span className="recipe-time">Time: {recipe.prepTime} + {recipe.cookTime}</span>
                        <span className="recipe-season">Best in: {recipe.season}</span>
                      </div>
                    )}
                    
                    <a href={`#${recipe.slug}`} className="view-recipe-btn">View Recipe</a>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-recipes">
              <div className="no-recipes-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="lucide-icon">
                  <path d="M6 13.87A4 4 0 0 1 7.41 6a5.11 5.11 0 0 1 1.05-1.54 5 5 0 0 1 7.08 0A5.11 5.11 0 0 1 16.59 6 4 4 0 0 1 18 13.87V21H6Z"></path>
                  <line x1="6" y1="17" x2="18" y2="17"></line>
                </svg>
              </div>
              <h3>We couldn't find any recipes</h3>
              <p>It seems we're still cultivating those particular flavors. Try different keywords or browse our seasonal collections below.</p>
              <div className="recipe-suggestions">
                <button onClick={() => handleCategoryChange('bucks-products')} className="suggestion-btn">
                  Buck's Products
                </button>
                <button onClick={() => handleCategoryChange('customer-recipes')} className="suggestion-btn">
                  Seasonal Recipes
                </button>
                <button onClick={() => {
                  setSearchTerm('');
                  fetchRecipes(activeCategory, '');
                }} className="suggestion-btn">
                  View All Recipes
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Recipe Details Sections */}
      {filteredRecipes.map(recipe => (
        <section className="recipe-detail-section" id={recipe.slug} key={`detail-${recipe.id}`}>
          <div className="recipe-detail-container">
            <div className="recipe-header">
              <h2>{recipe.title}</h2>
              <p className="recipe-detail-description">{recipe.description}</p>
            </div>
            
            <div className="recipe-detail-content">
              <div className="recipe-detail-image">
                <img src={recipe.image} alt={recipe.title} />
                
                {recipe.frontendCategory === 'customer-recipes' && (
                  <div className="recipe-specs">
                    <div className="spec-item">
                      <span className="spec-label">Difficulty</span>
                      <span className="spec-value">{recipe.difficulty}</span>
                    </div>
                    <div className="spec-item">
                      <span className="spec-label">Prep Time</span>
                      <span className="spec-value">{recipe.prepTime}</span>
                    </div>
                    <div className="spec-item">
                      <span className="spec-label">Cook Time</span>
                      <span className="spec-value">{recipe.cookTime}</span>
                    </div>
                    <div className="spec-item">
                      <span className="spec-label">Servings</span>
                      <span className="spec-value">{recipe.servings}</span>
                    </div>
                    <div className="spec-item">
                      <span className="spec-label">Best Season</span>
                      <span className="spec-value">{recipe.season}</span>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="recipe-detail-info">
                <div className="recipe-ingredients">
                  <h3>Ingredients</h3>
                  <ul>
                    {recipe.ingredients.map((ingredient, index) => (
                      <li key={index}>{ingredient}</li>
                    ))}
                  </ul>
                </div>
                
                <div className="recipe-method">
                  <h3>{recipe.frontendCategory === 'bucks-products' ? 'How We Make It' : 'Instructions'}</h3>
                  <ol>
                    {recipe.frontendCategory === 'bucks-products' 
                      ? recipe.method.map((step, index) => (
                          <li key={index}>{step}</li>
                        ))
                      : recipe.instructions.map((step, index) => (
                          <li key={index}>{step}</li>
                        ))
                    }
                  </ol>
                </div>
                
                {recipe.frontendCategory === 'customer-recipes' && recipe.tips && recipe.tips.length > 0 && (
                  <div className="recipe-tips">
                    <h3>Chef's Tips</h3>
                    <ul>
                      {recipe.tips.map((tip, index) => (
                        <li key={index}>{tip}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                <div className="share-print">
                  <button className="share-btn">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="18" cy="5" r="3"></circle>
                      <circle cx="6" cy="12" r="3"></circle>
                      <circle cx="18" cy="19" r="3"></circle>
                      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
                      <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
                    </svg>
                    Share Recipe
                  </button>
                  <button className="print-btn">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 6 2 18 2 18 9"></polyline>
                      <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                      <rect x="6" y="14" width="12" height="8"></rect>
                    </svg>
                    Print Recipe
                  </button>
                </div>
              </div>
            </div>
            
            <div className="back-to-top">
              <a href="#" className="back-link">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="19" x2="12" y2="5"></line>
                  <polyline points="5 12 12 5 19 12"></polyline>
                </svg>
                Back to recipes
              </a>
            </div>
          </div>
        </section>
      ))}

      {/* Newsletter Section - For Recipe Updates */}
      <section className="recipes-newsletter">
        <div className="newsletter-container">
          <div className="newsletter-content">
            <h2>Get Seasonal Recipes</h2>
            <p>Subscribe to receive new seasonal recipes, cooking tips, and exclusive offers from Buck's Farmstand.</p>
            <div className="newsletter-form">
              <input type="email" placeholder="Your email address" className="newsletter-input" />
              <button className="newsletter-btn">Subscribe</button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default BucksRecipes;